import { createFileRoute } from "@tanstack/react-router";
import { Instagram, Facebook, Linkedin, Play } from "lucide-react";
import { Section, SectionHeading } from "@/components/site/Section";
import { CtaBand } from "@/components/site/CtaBand";
import { JsonLd } from "@/components/site/JsonLd";
import { SocialPosts } from "@/components/site/SocialPosts";
import { meta } from "@/lib/schema";
import { overviewKeywords } from "@/lib/localKeywords";
import { media } from "@/data/media";
import { site } from "@/data/site";
import { TiktokIcon } from "@/components/site/BrandIcons";

const title = "Vlog: verhuizingen in actie & verhuistips | Vakkundig";
const description =
  "Bekijk video's van Vakkundig Verhuizingen & Transport: verhuislift in actie, kantoorverhuizingen, ontruimingen en handige verhuistips. Volg ons op Instagram, Facebook en LinkedIn.";

const videos = media.filter((m) => m.type === "video");

const channels = [
  { label: "Instagram", href: site.social.instagram, Icon: Instagram, text: "Reels & stories van onze klussen" },
  { label: "Facebook", href: site.social.facebook, Icon: Facebook, text: "Video's, updates en reviews" },
  { label: "LinkedIn", href: site.social.linkedin, Icon: Linkedin, text: "Zakelijke verhuizingen & projecten" },
  { label: "TikTok", href: site.social.tiktok, Icon: TiktokIcon, text: "Korte video's van onze verhuizingen" },
];

export const Route = createFileRoute("/vlog")({
  head: () =>
    meta({
      title,
      description,
      path: "/vlog",
      image: "verhuislift",
      keywords: overviewKeywords(["verhuizing video", "verhuislift video", "verhuistips vlog"]),
    }),
  component: VlogPage,
});

function VlogPage() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: videos.map((v, i) => ({
      "@type": "VideoObject",
      position: i + 1,
      name: v.caption ?? v.alt,
      description: v.alt,
      thumbnailUrl: v.poster,
      contentUrl: v.src,
      uploadDate: "2026-09-01",
    })),
  };

  return (
    <>
      <JsonLd data={schema} />
      <Section tone="navy">
        <SectionHeading
          as="h1"
          eyebrow="Vlog"
          title="Vakkundig in actie"
          intro="Echte beelden van onze verhuizingen, de verhuislift, ontruimingen en evenementen — plus handige tips. Nieuwe video's plaatsen we eerst op onze sociale kanalen."
          inverted
        />
        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          {channels.map(({ label, href, Icon, text }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl border border-primary-foreground/20 bg-primary-foreground/10 p-4 text-primary-foreground transition hover:bg-primary-foreground/20"
            >
              <Icon className="h-6 w-6 shrink-0" />
              <span>
                <span className="block font-semibold">Volg ons op {label}</span>
                <span className="text-sm opacity-80">{text}</span>
              </span>
            </a>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Video's" title="Onze nieuwste klussen" />
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {videos.map((v) => (
            <figure key={v.src} className="overflow-hidden rounded-xl bg-muted shadow-sm">
              <video
                src={v.src}
                poster={v.poster}
                controls
                playsInline
                preload="none"
                className="aspect-[9/16] w-full object-cover"
                aria-label={v.alt}
              />
              {v.caption && (
                <figcaption className="flex items-center gap-2 p-3 text-sm text-foreground">
                  <Play className="h-4 w-4 shrink-0 text-accent" />
                  {v.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading
          eyebrow="Social media"
          title="Onze berichten op Instagram, Facebook en LinkedIn"
          intro="Een greep uit wat we al geplaatst hebben. Filter per kanaal en bekijk het bericht op het platform."
        />
        <SocialPosts />
      </Section>

      <CtaBand />
    </>
  );
}
